import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import { DownloadFile } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { API, constructApiUrl } from '../../../api/apis.config'
import { getCenterProgramDocuments } from '../../../api/centers/center-program-documents/get-center-program-documents'
import { CenterProgramDocuments } from '../../../components/pages/centers/center-costs/center-program-documents'
import CenterProgramsAndActivitiesPlanner from '../../../components/pages/common/center-programs-and-activities-calendar'
import useAffectedStaff from '../../../lib/hooks/use-affected-staff'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import useTranslate from '../../../lib/hooks/use-translate'
import type { AffectedStaffMap } from '../../../lib/types/models/affectedStaff/affected-staff'

type StaffJourneyCenter = NonNullable<
  NonNullable<AffectedStaffMap['FULL']['staffRequest']>['centers']
>[number]

function StaffCenterActivityProgram({ center }: { center: StaffJourneyCenter }) {
  const { data: programDocuments } = useQuery({
    queryKey: ['center-program-documents', center.id],
    queryFn: () => getCenterProgramDocuments(center.id),
    initialData: []
  })

  // an uploaded program PDF replaces the Orion activity table
  if (programDocuments.length > 0)
    return <CenterProgramDocuments centerId={center.id} mode="readonly" olType={center.ol?.type} />

  return (
    <CenterProgramsAndActivitiesPlanner
      center={center}
      mode="staff-view"
      currency={center.ol?.currency}
    />
  )
}

export default function StaffPersonalSpaceJourneyDetailsActivitiesAndBudget() {
  const { t } = useTranslate()
  const { downloadDocuments } = useDownloadDocuments()
  const { affectedStaff } = useAffectedStaff()

  const center = affectedStaff?.staffRequest?.centers?.[0]

  const { data: programDocuments } = useQuery({
    queryKey: ['center-program-documents', center?.id],
    queryFn: () => getCenterProgramDocuments(center!.id),
    initialData: [],
    enabled: !!center
  })

  // staff is not affected to any center
  if (!center) return <div>Le staff n'est affecté à aucun centre</div>

  return (
    <div>
      <Typography variant="h3" color={tailwindColors.black.DEFAULT}>
        {t('staff.personal.space.my.journeys.details.activities.and.budget')}
      </Typography>
      <div className="h-4" />
      <InnerBox className="rounded-xl">
        {programDocuments.length === 0 && (
          <div className="flex items-center gap-2 mobile:flex-col mobile:items-start ">
            <Button
              icon={DownloadFile}
              variant="ghost"
              className="mobile:w-full mobile:justify-start"
              label={t(
                'staff.personal.space.my.journeys.details.tab.home.activities.download.program'
              )}
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(API.centers.printCenterActivityProgram.url, {
                    id: center.id
                  })
                })
              }
            />
            <Button
              icon={DownloadFile}
              variant="ghost"
              size="medium"
              className="mobile:w-full "
              label={t(
                'staff.personal.space.my.journeys.details.tab.home.activities.download.program.with.budget'
              )}
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(API.centers.printCenterActivityProgramWithBudget.url, {
                    id: center.id
                  })
                })
              }
            />
          </div>
        )}
        <div className="h-5" />
        {affectedStaff?.staffRequest?.centers?.map((center) => (
          <StaffCenterActivityProgram key={center.id} center={center} />
        ))}
      </InnerBox>
    </div>
  )
}
